import { Close, Visibility, VisibilityOff, Check } from "@mui/icons-material";
import { Container, Dialog, DialogContent, DialogTitle, Grid, IconButton, TextField, useMediaQuery, useTheme } from "@mui/material";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { showAlert } from "../../redux/create-actions/AlertAction";
import { closeChangePassModal, load } from "../../redux/create-actions/ChangePassAction";
import { closeLoadingApi, openLoadingApi } from "../../redux/create-actions/LoadingAction";
import Color from "../Color";
import Typography from "../theme-onboarding/Typography";
import Button from "../theme-onboarding/Button";
import Storage from "../../services/Storage";
import { graqhUserFirstChangePass } from "../../services/Authenticate";

const gridText = { textAlign: 'center' };
const buttonGroup = { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pt: 2 };
const buttonConfirm = {
  color: Color.Button,
  backgroundColor: Color.Main,
  '&:hover': { background: Color.Button, color: Color.Main }
};
const buttonClose = {
  color: Color.Button,
  backgroundColor: Color.Dangerous,
  '&:hover': { background: Color.Button, color: Color.Dangerous }
};

export default function ChangePasswordModal() {
  const theme = useTheme();
  const matchDownMD = useMediaQuery(theme.breakpoints.down('md'));
  const [showDefaultPassword, setShowDefaultPassword] = useState(false);
  const [defaultPassword, setDefaultPassword] = useState('');
  const [isDefaultPasswordError, setIsDefaultPasswordError] = useState(false);
  const [defaultPasswordErrorText, setDefaultPasswordErrorText] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [isPasswordError, setIsPasswordError] = useState(false);
  const [passwordErrorText, setPasswordErrorText] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isConfirmPasswordError, setIsConfirmPasswordError] = useState(false);
  const [confirmPasswordErrorText, setConfirmPasswordErrorText] = useState('');

  const data = useSelector(state => state.first.data);
  const dispatch = useDispatch();

  const handleClickShowDefaultPassword = () => {
    setShowDefaultPassword(!showDefaultPassword);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  function handleValidateDefaultPassword(password) {
    if (password === "") {
      setDefaultPassword(password);
      setIsDefaultPasswordError(true);
      setDefaultPasswordErrorText("Error Password Format");
    } else {
      setDefaultPassword(password);
      setIsDefaultPasswordError(false);
      setDefaultPasswordErrorText("");
    }
  };

  const handleChangeDefaultPassword = (e) => {
    let value = e.target.value;
    handleValidateDefaultPassword(value);
  }

  function handleValidatePassword(password) {
    if (password === "") {
      setPassword(password);
      setIsPasswordError(true);
      setPasswordErrorText("Error Password Format");
    } else {
      setPassword(password);
      setIsPasswordError(false);
      setPasswordErrorText("");
    }
  };

  const handleChangePassword = (e) => {
    let value = e.target.value;
    handleValidatePassword(value);
  };

  function handleValidateConfirmPassword(password) {
    if (password === "") {
      setConfirmPassword(password);
      setIsConfirmPasswordError(true);
      setConfirmPasswordErrorText("Error Password Format");
    } else {
      setConfirmPassword(password);
      setIsConfirmPasswordError(false);
      setConfirmPasswordErrorText("");
    }
  };

  const handleChangeConfirmPassword = (e) => {
    let value = e.target.value;
    handleValidateConfirmPassword(value);
  };

  const handleClose = () => {
    dispatch(closeChangePassModal());
  }

  const handleConfirm = (e) => {
    e.preventDefault();
    let alert = {
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'center'
      },
      isOpen: true,
      severity: 'error',
      title: 'Error',
      message: '',
    };
    if ((!isDefaultPasswordError && !isPasswordError && !isConfirmPasswordError) && defaultPassword !== '' && password !== '' && confirmPassword !== '') {
      if (password === confirmPassword) {
        dispatch(openLoadingApi());
        let promise = graqhUserFirstChangePass(data.response.accessToken.token, defaultPassword, password);
        promise.then(result => {
          Storage.updateLocalAccessToken(data.response.accessToken.token);
          Storage.updateLocalRefreshToken(data.response.refreshToken.token);
          Storage.updateLocalProfile(JSON.stringify(data.response.customer));
          alert = { ...alert, message: "Change first password successful!", severity: 'success', title: 'Success' };
          dispatch(load());
          dispatch(closeLoadingApi());
          dispatch(closeChangePassModal());
          dispatch(showAlert(alert));
        }).catch(function (error) {
          const err = JSON.parse(JSON.stringify(error))
          alert = { ...alert, message: err.response.errors[0].message };
          dispatch(closeLoadingApi());
          dispatch(showAlert(alert));
        })
      } else {
        alert = { ...alert, message: 'Password and Confirm Password need to be the same' };
        dispatch(showAlert(alert));
      }
    } else {
      alert = { ...alert, message: 'Fields cannot be empty' };
      dispatch(showAlert(alert));
    }
  }

  return (
    <Container>
      <Dialog
        open={data.isOpen}
        onClose={handleClose}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="center">
            <Grid item xs={12} sx={gridText}>
              <Typography variant="h4" marked="center" gutterBottom color={Color.Main}> Please change default password in the first login </Typography>
            </Grid>
            <Grid item xs={12} sx={gridText}>
              <Typography variant="h5" marked="center" gutterBottom color={Color.Normal}> Default password has been sent to your email </Typography>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid component="form" container spacing={3} direction="column" justifyContent="center" sx={{ pt: 1, px: matchDownMD ? 1 : 5 }}>
            <Grid item >
              <TextField
                required
                id="default-password"
                name="default-password"
                type={showDefaultPassword ? 'text' : 'password'}
                label="Default Password"
                fullWidth
                variant="outlined"
                autoComplete="password"
                error={isDefaultPasswordError}
                helperText={defaultPasswordErrorText}
                onChange={handleChangeDefaultPassword}
                size={matchDownMD ? 'small' : "medium"}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle default password visibility"
                      onClick={handleClickShowDefaultPassword}
                      edge="end"
                      size={matchDownMD ? "small" : "medium"}
                    >
                      {showDefaultPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  )
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    bgcolor: theme.palette.background.default
                  },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: theme.palette.background.default
                  }
                }}
              />
            </Grid>
            <Grid item >
              <TextField
                required
                id="new-password"
                name="new-password"
                type={showPassword ? 'text' : 'password'}
                label="New Password"
                fullWidth
                variant="outlined"
                autoComplete="password"
                error={isPasswordError}
                helperText={passwordErrorText}
                onChange={handleChangePassword}
                size={matchDownMD ? 'small' : "medium"}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                      size={matchDownMD ? "small" : "medium"}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  )
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    bgcolor: theme.palette.background.default
                  },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: theme.palette.background.default
                  }
                }}
              />
            </Grid>
            <Grid item >
              <TextField
                required
                id="confirm-password"
                name="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                label="Confirm Password"
                fullWidth
                variant="outlined"
                autoComplete="password"
                error={isConfirmPasswordError}
                helperText={confirmPasswordErrorText}
                onChange={handleChangeConfirmPassword}
                size={matchDownMD ? 'small' : "medium"}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={handleClickShowConfirmPassword}
                      edge="end"
                      size={matchDownMD ? "small" : "medium"}
                    >
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  )
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    bgcolor: theme.palette.background.default
                  },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: theme.palette.background.default
                  }
                }}
              />
            </Grid>
            <Grid container spacing={2} sx={buttonGroup}>
              <Grid item >
                <Button variant="contained" size={matchDownMD ? 'small' : 'medium'} sx={buttonClose} onClick={handleClose} >
                  Close <Close fontSize='small'></Close>
                </Button>
              </Grid>
              <Grid item >
                <Button type="submit" variant="contained" size={matchDownMD ? 'small' : 'medium'} sx={buttonConfirm} onClick={handleConfirm} >
                  Confirm <Check fontSize='small'></Check>
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Container>
  )
}